import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { cms } from "@/lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

const T = (name, label, type = "text") => ({ name, label, type });

const SCHEMAS = {
  "case-studies": {
    title: "Case Studies", label: "title",
    fields: [
      T("title", "Title"), T("slug", "Slug"), T("client_type", "Client Type"),
      T("venue", "Venue"), T("location", "Location"), T("guest_count", "Guest Count"),
      T("year", "Year"), T("hero_image", "Hero Image URL"),
      T("overview", "Overview", "textarea"), T("lessons", "In Reflection", "textarea"),
      T("objectives", "Objectives (list)", "json"),
      T("cocktail_menu", "Cocktail Menu [{name,notes}]", "json"),
      T("gallery", "Gallery image URLs (list)", "json"),
      T("testimonial", "Testimonial {quote,author,role}", "json"),
      T("featured", "Featured", "bool"), T("sort", "Sort", "number"),
    ],
  },
  journal: {
    title: "Journal", label: "title",
    fields: [
      T("title", "Title"), T("slug", "Slug"), T("category", "Category"),
      T("author", "Author"), T("read_time", "Read time"), T("cover_image", "Cover Image URL"),
      T("excerpt", "Excerpt", "textarea"), T("body", "Body (use blank lines between paragraphs)", "textarea"),
      T("featured", "Featured", "bool"), T("sort", "Sort", "number"),
    ],
  },
  "event-types": {
    title: "Event Types", label: "name",
    fields: [
      T("name", "Name"), T("slug", "Slug"), T("tagline", "Tagline"),
      T("hero_image", "Hero Image URL"), T("intro", "Intro", "textarea"),
      T("benefits", "Benefits [{title,body}]", "json"),
      T("planner_tips", "Planner Tips (list)", "json"),
      T("faqs", "FAQs [{q,a}]", "json"),
      T("gallery_tags", "Gallery Tags (list)", "json"),
      T("testimonial", "Testimonial {quote,author,role}", "json"),
      T("seo_title", "SEO Title"), T("seo_description", "SEO Description", "textarea"),
      T("sort", "Sort", "number"),
    ],
  },
  packages: {
    title: "Packages", label: "name",
    fields: [
      T("name", "Name"), T("tagline", "Tagline"), T("price_from", "Price From"),
      T("unit", "Unit"), T("inclusions", "Inclusions (list)", "json"),
      T("active", "Active", "bool"), T("sort", "Sort", "number"),
    ],
  },
  testimonials: {
    title: "Testimonials", label: "author",
    fields: [
      T("quote", "Quote", "textarea"), T("author", "Author"),
      T("role", "Role"), T("location", "Location"), T("sort", "Sort", "number"),
    ],
  },
  faqs: {
    title: "FAQs", label: "question",
    fields: [
      T("question", "Question"), T("answer", "Answer", "textarea"),
      T("category", "Category"), T("sort", "Sort", "number"),
    ],
  },
  "site-content": {
    title: "Site Copy", label: "label",
    fields: [T("key", "Key"), T("label", "Label"), T("value", "Value", "textarea")],
  },
};

function emptyDoc(schema) {
  const d = {};
  schema.fields.forEach((f) => {
    d[f.name] = f.type === "bool" ? false : f.type === "number" ? 0 : f.type === "json" ? [] : "";
  });
  return d;
}

export default function ContentManager() {
  const { kind } = useParams();
  const schema = SCHEMAS[kind];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => cms.list(kind).then(setItems).catch(() => {});
  useEffect(() => { setEditing(null); load(); /* eslint-disable-next-line */ }, [kind]);

  if (!schema) return <div className="text-aria-muted">Unknown content type.</div>;

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await cms.remove(kind, id);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={`admin-content-${kind}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="eyebrow mb-3">Content</div>
          <h1 className="font-display text-5xl text-aria-ink">{schema.title}</h1>
        </div>
        <button onClick={() => setEditing(emptyDoc(schema))} data-testid="content-new" className="flex items-center gap-2 bg-aria-ink px-6 py-3 text-[0.68rem] uppercase tracking-[0.2em] text-aria-ivory">
          <Plus className="h-4 w-4" /> New
        </button>
      </div>

      <div className="mt-8 divide-y divide-aria-travertine border-y border-aria-travertine bg-aria-ivory">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between px-5 py-4">
            <button onClick={() => setEditing(it)} data-testid={`content-edit-${it.id}`} className="text-left">
              <span className="font-display text-xl text-aria-ink">{it[schema.label] || it.title || it.name || "Untitled"}</span>
            </button>
            <button onClick={() => remove(it.id)} className="text-aria-muted hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {items.length === 0 && <div className="px-5 py-10 text-center text-aria-muted">Nothing yet. Create your first entry.</div>}
      </div>

      {editing && <Editor kind={kind} schema={schema} doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function Editor({ kind, schema, doc, onClose, onSaved }) {
  const initial = useMemo(() => {
    const d = { ...emptyDoc(schema), ...doc };
    schema.fields.forEach((f) => {
      if (f.type === "json") d[f.name] = JSON.stringify(doc[f.name] ?? (Array.isArray(emptyDoc(schema)[f.name]) ? [] : {}), null, 2);
    });
    return d;
  }, [doc, schema]);
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form };
    for (const f of schema.fields) {
      if (f.type === "json") {
        try { payload[f.name] = JSON.parse(form[f.name] || "[]"); }
        catch { toast.error(`Invalid JSON in "${f.label}"`); return; }
      }
      if (f.type === "number") payload[f.name] = Number(form[f.name]) || 0;
    }
    try {
      if (doc.id) await cms.update(kind, doc.id, payload);
      else await cms.create(kind, payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const input = "w-full border border-aria-travertine bg-aria-ivory px-3 py-2 text-sm text-aria-ink focus:border-aria-brass focus:outline-none";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-aria-ink/40" onClick={onClose}>
      <div className="h-full w-full max-w-2xl overflow-y-auto bg-aria-ivory p-8 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="content-editor">
        <div className="mb-8 flex items-center justify-between">
          <h2 className="font-display text-3xl text-aria-ink">{doc.id ? "Edit" : "New"} · {schema.title}</h2>
          <button onClick={onClose}><X className="h-6 w-6 text-aria-ink" /></button>
        </div>
        <div className="space-y-5">
          {schema.fields.map((f) => (
            <label key={f.name} className="block">
              <span className="mb-1.5 block text-[0.6rem] uppercase tracking-[0.16em] text-aria-muted">{f.label}</span>
              {f.type === "textarea" && <textarea rows={4} className={`${input} resize-y`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />}
              {f.type === "json" && <textarea rows={6} className={`${input} resize-y font-mono text-xs`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />}
              {f.type === "bool" && (
                <button type="button" onClick={() => set(f.name, !form[f.name])} className={`px-4 py-2 text-xs uppercase tracking-[0.15em] ${form[f.name] ? "bg-aria-ink text-aria-ivory" : "border border-aria-travertine text-aria-muted"}`}>
                  {form[f.name] ? "Yes" : "No"}
                </button>
              )}
              {(f.type === "text" || f.type === "number") && (
                <input type={f.type === "number" ? "number" : "text"} className={input} value={form[f.name] ?? ""} onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              )}
            </label>
          ))}
        </div>
        <button onClick={save} data-testid="content-save" className="mt-8 w-full bg-aria-ink py-4 text-[0.7rem] uppercase tracking-[0.22em] text-aria-ivory">Save</button>
      </div>
    </div>
  );
}
